import { ComponentRef, Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { IMapComponent } from "../../../map.models";
import { MapPopupComponent } from "./map-popup.component";
import type { ScreenPoint } from "@arcgis/core/core/types";

@Injectable()
export class MapPopupService {

    popupBlocked = false;
    currentPopup: MapPopupComponent;
    popupClosed = new Subject<any>();
    popupOpened = new Subject<MapPopupComponent>();
    throttleResize: any;
    throttle: any;
    originalPosition: ScreenPoint;

    show(component: any, point: ScreenPoint, data: any, target: MapPopupComponent, map: IMapComponent): Observable<ComponentRef<any>> {
        if (this.popupBlocked) {
            return undefined;
        }
        this.originalPosition = point;
        this.currentPopup = target;
        target.setInnerComponent(component, data, map);
        clearTimeout(this.throttleResize);
        this.throttleResize = setTimeout(() => {
            this.popupOpened.next(this.currentPopup);
            target.setOnCloseHandler(e => this.close());
            this.reposition(target, this.originalPosition);
            target.onSizeChanged = () => {
                this.reposition(target, this.originalPosition);
            };
            target.show();
        }, 250);
        return this.popupClosed;
    }

    close() {
        if (this.currentPopup) {
            this.popupClosed.next(this.currentPopup?.innerComponentRef);
            this.currentPopup.innerComponentRef.destroy();
            this.currentPopup.deflateInnerComponent();
            this.currentPopup = undefined;
        }
    }

    updatePosition(point?: any) {
        if (!this.currentPopup || !point || !point.x || !point.y) {
            return;
        }
        this.reposition(this.currentPopup, point);
    }

    getMapSize(popup: MapPopupComponent) {
        const element = popup.viewContainer.element.nativeElement as HTMLElement;
        if (!element) {
            return null;
        }
        const mapViewElement = element.parentElement;
        if (!mapViewElement) {
            return null;
        }
        return { width: mapViewElement.clientWidth,
            height: mapViewElement.clientHeight };
    }

    calcPosition(popup: MapPopupComponent, point: ScreenPoint): number {
        const size = this.getMapSize(popup);
        if (!size) {
            return null;
        }
        const popupWidth = popup.viewContainer.element.nativeElement.clientWidth;
        const popupHeight = popup.viewContainer.element.nativeElement.clientHeight;

        if (point.x + popupWidth <= size.width && point.y + popupHeight <= size.height) {
            return Position.BottomRight;
        }
        if (point.x + popupWidth <= size.width && point.y - popupHeight > 0) {
            return Position.TopRight;
        }
        if (point.x + (popupWidth / 2) <= size.width && point.y + popupHeight <= size.height) {
            return Position.Bottom;
        }
        if (point.y - popupHeight >= 0 && point.x + (popupWidth / 2) <= size.width) {
            return Position.Top;
        }
        if (point.y + popupHeight <= size.height && point.x - popupWidth >= 0) {
            return Position.TopLeft;
        }
        return Position.BottomLeft;
    }

    block() {
        this.popupBlocked = true;
    }

    unblock() {
        this.popupBlocked = false;
    }

    destroy() {
        this.close();
        this.popupClosed.unsubscribe();
    }

    private reposition(target, point: ScreenPoint): void {
        clearTimeout(this.throttle);
        this.throttle = setTimeout(() => {
            const pos = this.calcPosition(target, point);
            if (!pos) {
                return;
            }
            target.setPosition(point.x, point.y, pos);
        }, 10);
    }
}

export enum Position { TopLeft = 0, Left = 1, BottomLeft = 2, Top = 3, TopRight = 4, Right = 5, BottomRight = 6, Bottom = 7 }
